import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { practitionerService, Certificate } from '../../services/practitionerService';
import { TrainingAssignment, TrainingModule, AssignmentStatus } from '../../types/training';

export const TrainingDetail: React.FC = () => {
  const { assignmentId } = useParams<{ assignmentId: string }>();
  const navigate = useNavigate();
  const [assignment, setAssignment] = useState<TrainingAssignment | null>(null);
  const [module, setModule] = useState<TrainingModule | null>(null);
  const [certificate, setCertificate] = useState<Certificate | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadTrainingData = useCallback(async () => {
    if (!assignmentId) return;

    try {
      setLoading(true);
      setError(null);
      const assignmentData = await practitionerService.getAssignment(assignmentId);
      setAssignment(assignmentData);

      const moduleData = await practitionerService.getModule(assignmentData.moduleId);
      setModule(moduleData);

      // If training is completed, fetch the certificate
      if (assignmentData.status === AssignmentStatus.COMPLETED) {
        try {
          const certificates = await practitionerService.getMyCertificates();
          const moduleCert = certificates.find(c => c.moduleId === assignmentData.moduleId);
          if (moduleCert) {
            setCertificate(moduleCert);
          }
        } catch (certErr) {
          console.error('Error loading certificate:', certErr);
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to load training data');
      console.error('Error loading training data:', err);
    } finally {
      setLoading(false);
    }
  }, [assignmentId]);

  useEffect(() => {
    loadTrainingData();
  }, [loadTrainingData]);

  const handleViewContent = () => {
    navigate(`/training/${assignmentId}/content`);
  };

  const handleStartExam = () => {
    navigate(`/training/${assignmentId}/exam`);
  };

  const handleViewCertificate = async () => {
    if (certificate) {
      try {
        const response = await practitionerService.getCertificateUrl(certificate.id);
        window.open(response.url, '_blank');
      } catch (err) {
        console.error('Error getting certificate URL:', err);
        navigate('/certificates');
      }
    } else {
      navigate('/certificates');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !assignment || !module) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">{error || 'Training not found'}</p>
        <button
          onClick={() => navigate('/dashboard')}
          className="mt-4 text-blue-600 hover:text-blue-800"
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  const canTakeExam = assignment.progress?.contentViewed && !assignment.flagged;
  const hasAttempts = (assignment.progress?.examAttempts?.length ?? 0) > 0;
  const lastAttempt = hasAttempts ? assignment.progress.examAttempts[assignment.progress.examAttempts.length - 1] : null;

  return (
    <div className="px-4 py-6 sm:px-0">
      <div className="mb-6">
        <button
          onClick={() => navigate('/dashboard')}
          className="text-blue-600 hover:text-blue-800 mb-4 flex items-center"
        >
          <svg className="w-5 h-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Dashboard
        </button>
        <div className="flex items-center space-x-3">
          <h2 className="text-2xl font-bold text-gray-900">{module.title}</h2>
          {module.archived && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
              Archived
            </span>
          )}
        </div>
        <p className="mt-1 text-sm text-gray-600">{module.description}</p>
      </div>

      {assignment.flagged && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Training Flagged</h3>
              <p className="mt-1 text-sm text-red-700">
                You have reached the maximum number of exam attempts. Please contact an administrator for assistance.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Training Content */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Training Content</h3>
            <div className="space-y-3">
              {module.contentFiles?.map((file) => (
                <div key={file.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                  <div className="flex items-center">
                    <svg className="h-8 w-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-900">{file.fileName}</p>
                      <p className="text-xs text-gray-500">{file.fileType.toUpperCase()}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <button
              onClick={handleViewContent}
              disabled={module.archived}
              className={`mt-4 w-full px-4 py-2 rounded-lg transition-colors ${
                module.archived
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {module.archived ? 'Content Unavailable' : 'View Training Content'}
            </button>
          </div>

          {/* Exam Section */}
          {module.examId && (
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Assessment</h3>
              {!assignment.progress?.contentViewed ? (
                <div className="text-center py-4">
                  <p className="text-gray-600 mb-2">Please review the training content before taking the exam.</p>
                </div>
              ) : assignment.status === AssignmentStatus.COMPLETED ? (
                <div className="text-center py-4">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-green-100 mb-3">
                    <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="text-green-600 font-medium">Training Completed!</p>
                  {lastAttempt && (
                    <p className="text-sm text-gray-600 mt-1">Score: {lastAttempt.score.toFixed(2)}%</p>
                  )}
                  <button
                    onClick={handleViewCertificate}
                    className="mt-4 inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    View Certificate
                  </button>
                </div>
              ) : (
                <>
                  <p className="text-gray-600 mb-4">
                    Complete the exam to finish this training module.
                  </p>
                  <button
                    onClick={handleStartExam}
                    disabled={!canTakeExam}
                    className={`w-full px-4 py-2 rounded-lg transition-colors ${
                      canTakeExam
                        ? 'bg-green-600 text-white hover:bg-green-700'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    {hasAttempts ? 'Retake Exam' : 'Start Exam'}
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Progress Card */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Progress</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Content Viewed</span>
                {assignment.progress?.contentViewed ? (
                  <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                )}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Exam Attempts</span>
                <span className="text-sm font-medium text-gray-900">
                  {assignment.attempts} / {assignment.maxAttempts}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Status</span>
                <span className={`text-sm font-medium ${
                  assignment.status === AssignmentStatus.COMPLETED ? 'text-green-600' :
                  assignment.status === AssignmentStatus.FLAGGED ? 'text-red-600' :
                  'text-blue-600'
                }`}>
                  {assignment.status === AssignmentStatus.COMPLETED ? 'Completed' :
                   assignment.status === AssignmentStatus.FLAGGED ? 'Flagged' :
                   assignment.status === AssignmentStatus.IN_PROGRESS ? 'In Progress' :
                   'Not Started'}
                </span>
              </div>
            </div>
          </div>

          {/* Exam History */}
          {hasAttempts && assignment.progress?.examAttempts && (
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Exam History</h3>
              <div className="space-y-3">
                {assignment.progress.examAttempts.map((attempt) => (
                  <div key={attempt.attemptNumber} className="border-l-4 border-gray-300 pl-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          Attempt {attempt.attemptNumber}
                        </p>
                        <p className="text-xs text-gray-500">
                          {new Date(attempt.attemptedAt).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className={`text-sm font-medium ${attempt.passed ? 'text-green-600' : 'text-red-600'}`}>
                          {attempt.score.toFixed(2)}%
                        </p>
                        <p className="text-xs text-gray-500">
                          {attempt.passed ? 'Passed' : 'Failed'}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
